<template>
  <HoverPopover
    :trigger-label="triggerLabel"
    :panel-label="panelLabel"
    :side="side"
    :delay="delay"
    panel-class="max-w-sm"
    trigger-class="max-w-full"
    :cursor-class="cursorClass"
  >
    <slot />

    <template #content>
      <!-- The whole value and nothing else: the panel is `select-text`, so
           selecting the string is the interaction. `break-all` because these are
           paths and ids with no spaces to wrap at. -->
      <span class="block break-all font-mono text-xs text-foreground" data-testid="value-popover-value">
        {{ value }}
      </span>
    </template>
  </HoverPopover>
</template>

<script setup lang="ts">
// A shortened value with the whole thing in a popover you can reach into and
// select, over the same HoverPopover as the IP address popover. It replaced a
// native `title`, which cannot be selected, never appears on touch and is
// invisible to a keyboard user.
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import HoverPopover from "./HoverPopover.vue";

const props = withDefaults(
  defineProps<{
    // The full value. The trigger slot usually shows a shortened form of it.
    value: string;
    side?: "top" | "bottom" | "left" | "right";
    delay?: number;
    /** "cursor-default" where the trigger reveals a value rather than acting. */
    cursorClass?: string;
  }>(),
  { side: "bottom", delay: 200, cursorClass: "cursor-pointer" },
);

const { t } = useI18n();

// Not inline in the template: a template `t` resolves through the app's global
// properties, which a test's mock shadows, so an inline call renders the bare key.
const triggerLabel = computed(() => t("common.valuePopover.trigger", { value: props.value }));
const panelLabel = computed(() => t("common.valuePopover.label"));
</script>
